/**
 * Per-source power AVAILABILITY resolver — the evidence-grounded "how much of
 * each source could this site actually get" input to the phase power plan.
 *
 * HONESTY CONTRACT (mirrors the max-power ceilings): UNDOCUMENTED IS NEVER
 * UNLIMITED — a source with no documented availability resolves
 * `status: 'undocumented'` with `availableMW: null`; nothing here invents a
 * number. Every rung labels its basis; proxies are labeled proxies.
 *
 * GRID precedence (first hit wins):
 *   1. `sed.grid.data.capacityDetail.mw` — the structured utility-letter
 *      figure (the transform used to destroy it). Accepted from a VERIFIED
 *      section or a user-authored `verified-from-context` section (with a
 *      "user-authored indication" disclosure — the screening prefill's gate
 *      deliberately excludes that status; this resolver's job is to surface
 *      the user's own letter, labeled).
 *   2. `sed.transmissionHeadroom.data.atcMw` (provider) — honest-null today;
 *      future-proofing, not fabrication.
 *   3. HIFLD voltage-class proxy via `inferCapacityProxyMw` — status 'proxy',
 *      with the standing "not nameplate ATC" label.
 *   4. undocumented.
 *
 * GAS is ONE SHARED POOL across recip + gas-turbine (same molecules):
 *   1. Promoted scenario `availableGasCapacityDthPerDay` (written only under
 *      the firm-transport gate), derated to the documented minimum delivery
 *      pressure when computable (conservative bound: derated − uncertainty).
 *   2. SED KMZ/authored mass-flow with an explicit firm site-delivery basis,
 *      converted via `convertFirmGasCapacityToDthPerDay` (HHV-aware).
 *   Converted to MW through `resolveGasHeatRate` (labeled precedence chain).
 *
 * battery → not-applicable (storage draws no fuel and imports no firm supply);
 * solar/wind/smr/large-nuclear → undocumented in v1 (resource/licensing
 * availability models don't exist yet — bounded by user ranges only).
 */
import type { PhasePowerComponentKey } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { GenerationImpactScenarioSnapshot } from '../../types/generationImpact';
import { isVerifiedGridSection } from '../gridClaimHonesty';
import { utilityPipelineProjectionIsCurrent } from '../sed/utilityPipelineDocumentProjection';
import { inferCapacityProxyMw, parseVoltageKv } from '../coverage/utilityCoverageFill';
import {
  convertFirmGasCapacityToDthPerDay,
  explicitFirmSiteDeliveryBasis,
} from '../generationImpact/screeningEvidencePrefill';
import { deriveGasInterconnectDerate } from '../generationImpact/gasInterconnectDerate';
import {
  dthPerDayToDeliverableMW,
  resolveGasHeatRate,
  type GasHeatRateResolution,
} from './gasDeliverability';

export type SourceAvailabilityStatus = 'documented' | 'proxy' | 'undocumented' | 'not-applicable';

export interface SourceAvailability {
  status: SourceAvailabilityStatus;
  /** MW cap — null unless documented/proxy. NEVER invented. */
  availableMW: number | null;
  basis:
    | 'utility-capacity-detail'
    | 'atc-oasis'
    | 'voltage-class-proxy'
    | 'firm-transport-scenario'
    | 'sed-firm-pipeline'
    | null;
  provenance: { source: string; sectionStatus?: string };
  citations: string[];
  disclosures: string[];
  /** Set when this source draws from a shared pool (gas-fired sources). */
  sharedConstraintId?: 'gas-fuel-pool';
}

export interface SharedAvailabilityConstraint {
  id: 'gas-fuel-pool';
  /** recip + gas-turbine — the same molecules serve both. */
  appliesTo: readonly PhasePowerComponentKey[];
  capMW: number;
  capDthPerDay: number;
  heatRate: GasHeatRateResolution;
  basis: 'firm-transport-scenario' | 'sed-firm-pipeline';
  citations: string[];
  disclosures: string[];
}

export interface PerSourceAvailabilityResolution {
  perSource: Record<PhasePowerComponentKey, SourceAvailability>;
  sharedConstraints: SharedAvailabilityConstraint[];
}

export interface ResolvePerSourceAvailabilityArgs {
  sed: SiteEvidenceDocument | null | undefined;
  promotedScenario: GenerationImpactScenarioSnapshot | null | undefined;
}

const GAS_FIRED: readonly PhasePowerComponentKey[] = ['recip', 'gas-turbine'];

function undocumented(disclosure: string): SourceAvailability {
  return {
    status: 'undocumented',
    availableMW: null,
    basis: null,
    provenance: { source: 'none' },
    citations: [],
    disclosures: [disclosure],
  };
}

/** Grid section acceptable for the STRUCTURED capacityDetail figure. */
function gridSectionUsable(
  sed: SiteEvidenceDocument | null | undefined,
): { usable: boolean; userAuthored: boolean; sectionStatus?: string } {
  const grid = sed?.grid;
  if (!grid?.data) return { usable: false, userAuthored: false };
  if (isVerifiedGridSection(grid)) {
    return { usable: true, userAuthored: false, sectionStatus: grid.status };
  }
  if (grid.status === 'verified-from-context') {
    return { usable: true, userAuthored: true, sectionStatus: grid.status };
  }
  return { usable: false, userAuthored: false, sectionStatus: grid.status };
}

function resolveGridAvailability(sed: SiteEvidenceDocument | null | undefined): SourceAvailability {
  const gate = gridSectionUsable(sed);
  const grid = sed?.grid?.data;

  // 1. Structured utility statement.
  const detail = grid?.capacityDetail;
  if (gate.usable && detail && Number.isFinite(detail.mw) && detail.mw > 0) {
    const disclosures: string[] = [];
    if (detail.basis === 'utility-indication') {
      disclosures.push('Utility service-letter figure is an INDICATION, not a commitment.');
    }
    if (gate.userAuthored) {
      disclosures.push('User-authored utility indication — not independently verified.');
    }
    if (grid?.interconnectionClass === 'non-firm' || grid?.interconnectionClass === 'energy-only') {
      disclosures.push(
        `Interconnection class is '${grid.interconnectionClass}' — the grid capacity credit (97%) assumes FIRM service; non-firm import carries materially less firm value on a 24/7 load.`,
      );
    }
    return {
      status: 'documented',
      availableMW: detail.mw,
      basis: 'utility-capacity-detail',
      provenance: { source: 'sed.grid.capacityDetail', sectionStatus: gate.sectionStatus },
      citations: [`Utility letter — stated available capacity ${detail.mw} MW (${detail.basis})`],
      disclosures,
    };
  }

  // 2. Provider ATC (honest-null today; never fabricated).
  const headroom = sed?.transmissionHeadroom;
  const atc = headroom?.data?.atcMw;
  if (
    typeof atc === 'number' && Number.isFinite(atc) && atc > 0 &&
    headroom?.data?.provenance === 'provider'
  ) {
    return {
      status: 'documented',
      availableMW: atc,
      basis: 'atc-oasis',
      provenance: { source: 'sed.transmissionHeadroom.atcMw', sectionStatus: headroom.status },
      citations: [`Provider ATC ${atc} MW`],
      disclosures: [],
    };
  }

  // 3. Voltage-class proxy (labeled — never persisted as documented).
  if (isVerifiedGridSection(sed?.grid)) {
    const kv = parseVoltageKv(sed?.grid?.data?.voltage);
    const proxyMw = kv != null ? inferCapacityProxyMw(kv) : null;
    if (proxyMw != null && proxyMw > 0) {
      return {
        status: 'proxy',
        availableMW: proxyMw,
        basis: 'voltage-class-proxy',
        provenance: { source: 'sed.grid.voltage', sectionStatus: sed?.grid?.status },
        citations: [`${kv} kV voltage class → ${proxyMw} MW line-capacity proxy`],
        disclosures: [
          `${proxyMw} MW is an HIFLD voltage-class proxy — not nameplate ATC. Nameplate headroom requires utility confirmation.`,
        ],
      };
    }
  }

  return undocumented(
    'No utility-stated capacity, provider ATC, or usable voltage class is documented — grid import availability is undocumented (undocumented is not unlimited).',
  );
}

interface GasPoolResolution {
  pool: SharedAvailabilityConstraint | null;
  /** Why no pool, for the per-source disclosure. */
  disclosure: string;
}

function resolveGasPool(args: ResolvePerSourceAvailabilityArgs): GasPoolResolution {
  const scenario = args.promotedScenario;
  const heatRate = resolveGasHeatRate(scenario);

  // 1. Promoted scenario firm transport (only ever written under the
  //    firm-transport-agreement gate), derated when computable.
  const scenarioDth = scenario?.input?.availableGasCapacityDthPerDay;
  if (typeof scenarioDth === 'number' && Number.isFinite(scenarioDth) && scenarioDth > 0) {
    let capDth = scenarioDth;
    const citations = [`Firm transport ${scenarioDth} Dth/day (promoted scenario input)`];
    const disclosures: string[] = [...heatRate.disclosures];
    const supply = scenario?.input?.projectDutyBasis?.fuelGas?.supply;
    if (supply) {
      const derate = deriveGasInterconnectDerate(
        supply,
        scenario?.input?.projectDutyBasis?.fuelGas?.documentedSiteSupplyMinimum,
      );
      if (derate.deratedDeliverableDthPerDay != null) {
        // Conservative one-sided bound at the documented minimum pressure.
        const conservative = derate.deratedDeliverableDthPerDay - derate.uncertaintyDthPerDay;
        if (Number.isFinite(conservative) && conservative > 0 && conservative < capDth) {
          capDth = conservative;
          citations.push(
            `Derated to ${Math.round(conservative)} Dth/day at the documented minimum delivery pressure (conservative bound)`,
          );
          disclosures.push(...derate.assumptions);
        }
      }
    }
    const capMW = dthPerDayToDeliverableMW(capDth, heatRate);
    if (capMW != null) {
      return {
        pool: {
          id: 'gas-fuel-pool',
          appliesTo: GAS_FIRED,
          capMW,
          capDthPerDay: capDth,
          heatRate,
          basis: 'firm-transport-scenario',
          citations,
          disclosures,
        },
        disclosure: '',
      };
    }
  }

  // 2. SED firm site-deliverable pipeline mass-flow (KMZ / authored).
  const infra = args.sed?.onSiteInfrastructure;
  const infraData = infra?.data;
  const sectionOk =
    infra?.status === 'verified'
    || infra?.status === 'verified-from-doc'
    || infra?.status === 'verified-from-kmz'
    || infra?.status === 'verified-from-context';
  if (sectionOk && infraData && args.sed && utilityPipelineProjectionIsCurrent(args.sed)) {
    const flow = infraData.nearestPipelineMassFlow;
    const value = flow?.value ?? infraData.nearestPipelineMassFlowValue;
    const unit = flow?.unit ?? infraData.nearestPipelineMassFlowUnit;
    const basis = flow?.basis ?? infraData.nearestPipelineMassFlowBasis;
    if (
      typeof value === 'number' && Number.isFinite(value) && value > 0 &&
      typeof unit === 'string' && explicitFirmSiteDeliveryBasis(basis ?? undefined)
    ) {
      const hhv = args.sed?.fuelSupply?.data?.qualitySpecs?.hhvMinBtuScf;
      const dth = convertFirmGasCapacityToDthPerDay(value, unit, {
        ...(typeof hhv === 'number' && Number.isFinite(hhv) && hhv > 0
          ? { documentedHhvBtuPerScf: hhv }
          : {}),
      });
      if (dth != null) {
        const capMW = dthPerDayToDeliverableMW(dth, heatRate);
        if (capMW != null) {
          const disclosures = [...heatRate.disclosures];
          if (typeof hhv === 'number' && /mmscf/i.test(unit)) {
            disclosures.push(
              `Volumetric pipeline figure converted at the documented ${hhv} Btu/scf HHV minimum (fuelSupply.qualitySpecs — conservative).`,
            );
          }
          return {
            pool: {
              id: 'gas-fuel-pool',
              appliesTo: GAS_FIRED,
              capMW,
              capDthPerDay: dth,
              heatRate,
              basis: 'sed-firm-pipeline',
              citations: [
                `Firm site-deliverable pipeline capacity ${value} ${unit} (${basis ?? 'basis on file'})`,
              ],
              disclosures,
            },
            disclosure: '',
          };
        }
      }
    }
  }

  return {
    pool: null,
    disclosure:
      'No firm, site-deliverable gas capacity is documented (firm transport agreement or firm site-delivery pipeline basis required) — gas-fired availability is undocumented, and undocumented is not unlimited.',
  };
}

export function resolvePerSourceAvailability(
  args: ResolvePerSourceAvailabilityArgs,
): PerSourceAvailabilityResolution {
  const gridAvailability = resolveGridAvailability(args.sed);
  const gas = resolveGasPool(args);

  const gasSource = (key: PhasePowerComponentKey): SourceAvailability => {
    if (!gas.pool) return undocumented(gas.disclosure);
    return {
      status: 'documented',
      availableMW: gas.pool.capMW,
      basis: gas.pool.basis,
      provenance: { source: gas.pool.basis },
      citations: gas.pool.citations,
      disclosures: [
        ...gas.pool.disclosures,
        `Shared fuel pool: recip + gas turbines draw the SAME ${Math.round(gas.pool.capDthPerDay)} Dth/day — their combined gas-fired MW is capped jointly at ${round1(gas.pool.capMW)} MW, not each.`,
      ],
      sharedConstraintId: gas.pool.id,
    };
  };

  const perSource: Record<PhasePowerComponentKey, SourceAvailability> = {
    grid: gridAvailability,
    recip: gasSource('recip'),
    'gas-turbine': gasSource('gas-turbine'),
    battery: {
      status: 'not-applicable',
      availableMW: null,
      basis: null,
      provenance: { source: 'none' },
      citations: [],
      disclosures: [
        'Storage buffers energy; it draws no fuel and imports no firm supply — sized by the user range only.',
      ],
    },
    'solar-bess': undocumented(
      'Solar resource-to-MW availability is not modeled in v1 — bounded by the declared range only.',
    ),
    'wind-bess': undocumented(
      'Wind resource-to-MW availability is not modeled in v1 — bounded by the declared range only.',
    ),
    smr: undocumented(
      'SMR deployable-MW availability (licensing pathway, EPZ) is not modeled in v1 — bounded by the declared range only.',
    ),
    'large-nuclear': undocumented(
      'Large-nuclear deployable-MW availability is not modeled in v1 — bounded by the declared range only.',
    ),
  };

  return {
    perSource,
    sharedConstraints: gas.pool ? [gas.pool] : [],
  };
}

function round1(v: number): number {
  return Math.round(v * 10) / 10;
}
